import { provideLocal } from '@vueuse/core';
import useConsolidatedLatchkey from '~/modules/latchkey/composables/useConsolidatedLatchkey';

import { useProductType } from '~/composables/useProductType';

import useColor from '~/modules/color/composables/useColor';
import useSize from '~/modules/size/composables/useSize';
import useUpc from '~/modules/upc/composables/useUpc';
import { PRODUCT_PRICE_REPOSITORY } from '~/modules/product/constants/providers';
import { usePrice } from './mcl-utils/usePrice';
import { useInitPrice } from './mcl-utils/useInitPrice';
import { usePriceParser } from './mcl-utils/usePriceParser';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';

const productTraits = () => ({
  selectedColor: useColor()?.selectedColor,
  selectedSize: useSize()?.selectedSize,
  selectedUpcId: useUpc()?.selectedUpcIdOrNull,
});

/**
 * Get selected price based on selected product traits (i.e. color, size, types, etc.)
 *
 * @description
 * This composable is dual brand friendly and handles getting price selection.
 *
 * USE CASES:
 *
 * 1) Consolidated Latchkey Use Case
 *   - DESCRIPTION: The composable is called to handel the price selection use case, when available
 *   - PARAMS USED:
 *     a) id (Product Id)
 * 2) Standard Use Case
 *   - DESCRIPTION:
 *   - PARAMS USED: Uses the standard methodology of initializing, parsing, and retrieving selected product prices.
 *     a) id (Product Id)
 *     b) product (Product Data)
 */
export const useProductPriceProvide = ({ id, product }: { id: ProductId; product: Product }) => {
  const { isConsolidatedLatchKey } = useProductType();
  const { isShopTheSetExpEnabled } = useFeatureEligibility();

  if (isConsolidatedLatchKey) {
    /** USE CASE #1 * */
    const { selectedPrice } = useConsolidatedLatchkey();
    provideLocal(PRODUCT_PRICE_REPOSITORY, selectedPrice);
    return;
  }
  if (isShopTheSetExpEnabled) {
    const selectedMemberId = useSelectedMemberId();
    const { selectedPrice } = usePrice({
      id: selectedMemberId,
      ...productTraits(),
    });
    provideLocal(PRODUCT_PRICE_REPOSITORY, selectedPrice);
    return;
  }

  /** USE CASE #2 * */

  // set product price data cache
  useInitPrice({ id, data: usePriceParser(product) });
  // return selected price from the usePrice composable
  const { selectedPrice } = usePrice({
    id,
    ...productTraits(),
  });

  provideLocal(PRODUCT_PRICE_REPOSITORY, selectedPrice);
};
